import { useCallback, useMemo } from 'react'

import _ from 'lodash'
import { useSelector, useDispatch } from 'react-redux'
import { isUserRoleApprover } from 'Components/utils/roles'
import { productState } from 'Config/constants'
import { setNeedsRecalculateMin } from 'Redux/purchases/actions'

export const SUM_ONLY_PRODUCTS = true

const useOrderMin = () => {
  const user = useSelector(({ auth }) => auth.user)
  const center = useSelector(state => state.auth.selectedCenter)
  const cart = useSelector(state => state.purchase.cart)
  const dispatch = useDispatch()

  const { showPrice, requiresApproval } = center

  const [allProducts] = useMemo(
    () =>
      _.map(cart)?.reduce(
        ([ok, notOk], elem) =>
          elem.state === productState.noAvailable ? [ok, [...notOk, elem]] : [[...ok, elem], notOk],
        [[], []]
      ),
    [cart]
  )

  const okProducts = useMemo(() => (allProducts ? allProducts.filter(el => el.amount > 0) : []), [allProducts])

  const productsToShow = useMemo(() => _.groupBy(okProducts, 'provider.name'), [okProducts])

  const getIsExtraService = useCallback(el => {
    if (el.isServicioExtra || (el.extraServiceDescription && el.extraServiceDescription !== '')) {
      return true
    }
    return false
  }, [])

  const totalByProvider = useMemo(() => {
    let baseTotalList
    if (SUM_ONLY_PRODUCTS === true) {
      baseTotalList = okProducts.filter(e => !getIsExtraService(e))
    } else {
      baseTotalList = [...okProducts]
    }
    return _(baseTotalList)
      .groupBy('provider.name')
      .map((item, key) => ({
        index: key,
        total: _.sumBy(item, 'total'),
      }))
      .value()
  }, [getIsExtraService, okProducts])

  const getProviderTotals = useCallback(
    prodToShow => {
      if (SUM_ONLY_PRODUCTS === true) {
        return (prodToShow || [])
          .filter(p => !getIsExtraService(p))
          .map(({ total }) => total || 0)
          ?.reduce((a, b) => a + b, 0)
      }
      return prodToShow?.map(({ total, extraService }) => total || extraService?.price || 0)?.reduce((a, b) => a + b, 0)
    },
    [getIsExtraService]
  )

  const minOrderByProvider = useMemo(() => {
    const providerMap = okProducts.reduce((acc, { provider }) => {
      acc[provider.name] = { orderMinimum: provider?.orderMinimum || null }
      return acc
    }, {})
    return providerMap
  }, [okProducts])

  const areAllServiceExtra = useMemo(() => {
    const allServExtra = okProducts.filter(el => getIsExtraService(el))
    if (okProducts?.length === allServExtra?.length) return true
    return false
  }, [getIsExtraService, okProducts])

  const getAreAllServiceExtraForProvider = useCallback(
    prov => {
      const allServExtra = okProducts.filter(el => getIsExtraService(el) && el?.provider?.name === prov)
      const allProviderProducts = okProducts.filter(el => el?.provider?.name === prov)
      if (allProviderProducts?.length === allServExtra?.length) return true
      return false
    },
    [getIsExtraService, okProducts]
  )

  const notMin = useMemo(() => {
    if (areAllServiceExtra) return false
    const result = Object.entries(minOrderByProvider).reduce((acc, [provider, data]) => {
      const totalItem = totalByProvider.find(tP => tP.index === provider)
      const total = totalItem?.total ?? 0
      const { orderMinimum } = data
      if (orderMinimum != null && orderMinimum - total > 0 && !getAreAllServiceExtraForProvider(provider)) {
        acc[provider] = {
          ...data,
          needsMore: orderMinimum - total,
        }
      }
      return acc
    }, {})
    return Object.keys(result)?.length > 0
  }, [areAllServiceExtra, getAreAllServiceExtraForProvider, minOrderByProvider, totalByProvider])

  const hastToShowErrorMinOrder = useMemo(() => {
    if (showPrice && notMin && !requiresApproval) {
      return true
    }
    return false
  }, [notMin, requiresApproval, showPrice])

  // if needs show message but not block purchase
  const getShowMinWithoutBlockPurchase = useCallback(
    prov => {
      const isApprover = isUserRoleApprover(user)
      return (
        !getAreAllServiceExtraForProvider(prov) &&
        !showPrice &&
        isApprover &&
        minOrderByProvider[prov]?.orderMinimum &&
        minOrderByProvider[prov]?.orderMinimum - totalByProvider.find(element => element.index === prov).total > 0
      )
    },
    [getAreAllServiceExtraForProvider, minOrderByProvider, showPrice, totalByProvider, user]
  )

  const calculateNeedsMinProvider = useCallback(
    prov => {
      const needs =
        !getAreAllServiceExtraForProvider(prov) &&
        showPrice &&
        !requiresApproval &&
        minOrderByProvider[prov]?.orderMinimum &&
        minOrderByProvider[prov]?.orderMinimum - totalByProvider.find(element => element.index === prov).total > 0
      if (needs) {
        dispatch(
          setNeedsRecalculateMin({
            provider: prov,
            needsMinByProvider:
              minOrderByProvider[prov]?.orderMinimum - totalByProvider.find(element => element.index === prov).total ||
              0,
          })
        )
      } else {
        dispatch(
          setNeedsRecalculateMin({
            provider: prov,
            needsMinByProvider: null,
          })
        )
      }
    },
    [dispatch, getAreAllServiceExtraForProvider, minOrderByProvider, requiresApproval, showPrice, totalByProvider]
  )

  return {
    notMin,
    minOrderByProvider,
    totalByProvider,
    productsToShow,
    hastToShowErrorMinOrder,
    getShowMinWithoutBlockPurchase,
    calculateNeedsMinProvider,
    getProviderTotals,
  }
}

export default useOrderMin
